'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import clsx from 'clsx';
import { useStore } from '@base-ui/utils/store';
import { styled } from '@mui/material/styles';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MenuOpen from '@mui/icons-material/MenuOpen';
import Menu from '@mui/icons-material/Menu';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useAdapterContext } from '@mui/x-scheduler-internals/use-adapter-context';
import { useEventCalendarStoreContext } from '@mui/x-scheduler-internals/use-event-calendar-store-context';
import { eventCalendarPreferenceSelectors, eventCalendarViewSelectors } from '@mui/x-scheduler-internals/event-calendar-selectors';
import { schedulerOtherSelectors } from '@mui/x-scheduler-internals/scheduler-selectors';
import { getWeekNumber } from '@mui/x-scheduler-internals/internals';
import { ViewSwitcher } from "./view-switcher/index.mjs";
import { PreferencesMenu } from "./preferences-menu/index.mjs";
import { useEventCalendarStyledContext } from "../EventCalendarStyledContext.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const HeaderToolbarRoot = styled('header', {
  name: 'MuiEventCalendar',
  slot: 'HeaderToolbar'
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  flexWrap: 'wrap'
}));
const HeaderToolbarActions = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'HeaderToolbarActions'
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(2)
}));
const HeaderToolbarDateNavigator = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'HeaderToolbarDateNavigator'
})(({
  theme
}) => ({
  display: 'flex',
  gap: theme.spacing(2)
}));
const HeaderToolbarLeftElement = styled('div', {
  name: 'MuiEventCalendar',
  slot: 'HeaderToolbarLeftElement'
})(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: theme.spacing(2)
}));
const HeaderToolbarLabel = styled('p', {
  name: 'MuiEventCalendar',
  slot: 'HeaderToolbarLabel'
})(({
  theme
}) => _extends({
  margin: 0
}, theme.typography.h6, {
  fontWeight: theme.typography.fontWeightBold,
  lineHeight: 1.4,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));
const HeaderToolbarWeekNumber = styled('span', {
  name: 'MuiEventCalendar',
  slot: 'HeaderToolbarWeekNumber'
})(({
  theme
}) => _extends({
  padding: theme.spacing(0.1, 0.7),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontWeight: theme.typography.fontWeightRegular,
  color: (theme.vars || theme).palette.text.primary,
  textAlign: 'center',
  backgroundColor: (theme.vars || theme).palette.grey[200]
}, theme.applyStyles('dark', {
  backgroundColor: (theme.vars || theme).palette.grey[900]
})));
export const HeaderToolbar = /*#__PURE__*/React.forwardRef(function HeaderToolbar(props, forwardedRef) {
  // Context hooks
  const store = useEventCalendarStoreContext();
  const {
    classes,
    localeText
  } = useEventCalendarStyledContext();
  const adapter = useAdapterContext();

  // Selector hooks
  const views = useStore(store, eventCalendarViewSelectors.views);
  const view = useStore(store, eventCalendarViewSelectors.view);
  const visibleDate = useStore(store, schedulerOtherSelectors.visibleDate);
  const isSidePanelOpen = useStore(store, eventCalendarPreferenceSelectors.isSidePanelOpen);
  const showWeekNumber = useStore(store, eventCalendarPreferenceSelectors.showWeekNumber);
  const weekStartsOn = useStore(store, eventCalendarPreferenceSelectors.weekStartsOn);
  const weekNumber = getWeekNumber(adapter, visibleDate, weekStartsOn);
  const showViewSwitcher = views.length > 1;
  const showWeekLabel = showWeekNumber && (view === 'week' || view === 'day');
  return /*#__PURE__*/_jsxs(HeaderToolbarRoot, _extends({
    ref: forwardedRef,
    "data-single-primary-action": !showViewSwitcher
  }, props, {
    className: clsx(props.className, classes.headerToolbar),
    children: [/*#__PURE__*/_jsxs(HeaderToolbarLeftElement, {
      className: classes.headerToolbarLeftElement,
      children: [/*#__PURE__*/_jsx(IconButton, {
        className: classes.headerToolbarSidePanelToggle,
        "aria-label": isSidePanelOpen ? localeText.closeSidePanel : localeText.openSidePanel,
        onClick: event => store.setPreferences({
          isSidePanelOpen: !isSidePanelOpen
        }, event.nativeEvent),
        children: isSidePanelOpen ? /*#__PURE__*/_jsx(MenuOpen, {}) : /*#__PURE__*/_jsx(Menu, {})
      }), /*#__PURE__*/_jsxs(HeaderToolbarLabel, {
        "aria-live": "polite",
        children: [adapter.format(visibleDate, 'monthFullLetter'), ' ', adapter.format(visibleDate, 'yearPadded'), showWeekLabel && /*#__PURE__*/_jsx(HeaderToolbarWeekNumber, {
          className: classes.headerToolbarWeekNumber,
          children: `${localeText.week} ${weekNumber}`
        })]
      })]
    }), /*#__PURE__*/_jsxs(HeaderToolbarActions, {
      className: classes.headerToolbarActions,
      children: [/*#__PURE__*/_jsxs(HeaderToolbarDateNavigator, {
        role: "navigation",
        className: classes.headerToolbarDateNavigator,
        children: [/*#__PURE__*/_jsx(IconButton, {
          className: classes.headerToolbarPreviousButton,
          onClick: store.goToPreviousVisibleDate,
          "aria-label": localeText.previousTimeSpan(view),
          children: /*#__PURE__*/_jsx(ChevronLeft, {})
        }), /*#__PURE__*/_jsx(IconButton, {
          className: classes.headerToolbarNextButton,
          onClick: store.goToNextVisibleDate,
          "aria-label": localeText.nextTimeSpan(view),
          children: /*#__PURE__*/_jsx(ChevronRight, {})
        })]
      }), /*#__PURE__*/_jsx(Button, {
        onClick: store.goToToday,
        children: localeText.today
      }), showViewSwitcher && /*#__PURE__*/_jsx(ViewSwitcher, {
        views: views,
        view: view,
        onViewChange: store.setView
      }), /*#__PURE__*/_jsx(PreferencesMenu, {})]
    })]
  }));
});
if (process.env.NODE_ENV !== "production") HeaderToolbar.displayName = "HeaderToolbar";